import moment from "moment";

export interface IKeyFigure {
    /** The number as shown, e.g. "88 198" or "0,7". */
    number: string;

    /** A unit that followed the number, e.g. "kr", "SEK" or "%". */
    unit?: string;
}

// A number (digits with spaces, dots or commas between them) followed by an optional unit
const numberWithUnit = /^([+-]?\d(?:[\d\s .,]*\d)?)\s*([^\d\s][^\d]*)?$/;

/**
 * Splits a key figure into number and unit for display.
 *
 * A number is formatted in the user's language (88198 -> "88 198", 0.7 -> "0,7" in Swedish).
 * A string is kept as the source sent it ("301 494 SEK"), only split into number and unit
 * when it is a number with a trailing unit; anything else is shown whole.
 */
export const formatKeyFigure = (value: unknown, locale: string = moment.locale()): IKeyFigure => {
    if (typeof value === "number") {
        return {
            number: Number.isFinite(value) ?
                new Intl.NumberFormat(locale, { maximumFractionDigits: 20 }).format(value) :
                value.toString(),
        };
    }

    const text = value === null || value === undefined ? "" : String(value).trim();
    const match = numberWithUnit.exec(text);

    if (!match) {
        return { number: text };
    }

    const unit = match[2] ? match[2].trim() : undefined;

    return unit ?
        { number: match[1].trim(), unit } :
        { number: match[1].trim() };
};

/**
 * Reads a number from a key figure value: a number as it is, or the first number in a
 * string such as "1 204 000 kr", "0,2" or "-4". Spaces group thousands; a lone comma or
 * point is the decimal mark. Undefined when there is no number.
 */
export const parseKeyNumber = (value: unknown): number | undefined => {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : undefined;
    }

    if (typeof value !== "string") {
        return undefined;
    }

    const match = /[+-]?\d[\d\s\u00a0\u202f]*(?:[.,]\d+)?/.exec(value);

    if (!match) {
        return undefined;
    }

    const number = parseFloat(match[0].replace(/[\s\u00a0\u202f]/g, "").replace(",", "."));

    return Number.isFinite(number) ? number : undefined;
};

/**
 * The change in per cent against the previous value (current − diff), in the user's
 * language: "+0,2" on 4,6 is "4 %". Empty when it can't be told or rounds to 0.
 */
export const formatDiffPercent = (current: unknown, diff: unknown, locale: string = moment.locale()): string => {
    const currentNumber = parseKeyNumber(current);
    const diffNumber = parseKeyNumber(diff);

    if (currentNumber === undefined || diffNumber === undefined) {
        return "";
    }

    const previous = currentNumber - diffNumber;

    if (previous === 0) {
        return "";
    }

    const ratio = diffNumber / Math.abs(previous);

    if (!Number.isFinite(ratio) || Math.round(ratio * 100) === 0) {
        return "";
    }

    return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(ratio);
};
